export interface EventCategory {
  id: number;
  name: string;
}

export interface City {
  id: number;
  name: string;
}

export interface Skill {
  id: number;
  name: string;
}

export interface Speaker {
  id: number;
  firstName: string;
  lastName: string;
  patronymic: string;
  photoUrl: string;
  about: string;
  skills: Skill[];
  /** 10–15 цифр с кодом страны, без пробелов и скобок: 77001234567 */
  phoneNumber?: string | null;
  /** Абсолютная ссылка на Instagram, до 2048 символов */
  instagramUrl?: string | null;
}

export interface PriceInfo {
  value: number;
  unicode: string;
}

export interface EventPrices {
  resident: PriceInfo;
  noResident: PriceInfo;
}

export interface Discount {
  title: string;
  description: string;
}

export interface EventClub {
  id: number;
  name: string;
  description?: string;
  logoUrl?: string;
}

export type EventStatus = "draft" | "published";

export interface Event {
  id: number;
  title: string;
  description: string;
  city: City;
  address: string;
  broadcastUrl: string;
  participantMax: number;
  registrationStartAt: string;
  registrationEndAt: string;
  format: "online" | "offline";
  startAt: string;
  endAt: string;
  calibers: string[];
  roles: string[];
  thumbnailImageUrl: string;
  largeImageUrl: string;
  participantCount: number;
  applicationCount?: number;
  category: EventCategory;
  favorite?: boolean;
  speakers: Speaker[];
  discount?: Discount;
  prices?: EventPrices;
  isPublicContent?: boolean;
  addressLocationUrl?: string;
  status: EventStatus;
  club?: EventClub;
  /** Состав участников сформирован заранее: в приложении запись закрыта */
  participantsFormed?: boolean;
}

export interface EventListItem {
  id: number;
  title: string;
  city: City;
  address: string;
  format: "online" | "offline";
  startAt: string;
  calibers: string[];
  roles: string[];
  thumbnailImageUrl: string;
  category: EventCategory;
  applicationCount: number;
  participantCount: number;
  status: EventStatus;
  club?: EventClub;
  /** Состав участников сформирован заранее: в приложении запись закрыта */
  participantsFormed?: boolean;
}

export interface EventListResponse {
  result: boolean;
  pageToken?: string;
  events: EventListItem[];
}

export interface EventResponse {
  result: boolean;
  event: Event;
}

export enum ResidentCaliber {
  VOSHOD = "voshod",
  SIYAY = "siyay",
  SOVER = "sover",
}

export enum UserRole {
  USER = "user",
  RESIDENT = "resident",
}

// Функция для получения русских названий категорий резидентов
export const getResidentDisplayName = (resident: ResidentCaliber): string => {
  switch (resident) {
    case ResidentCaliber.VOSHOD:
      return "Восходящая звезда";
    case ResidentCaliber.SIYAY:
      return "Сияющая звезда";
    case ResidentCaliber.SOVER:
      return "Совершенная звезда";
    default:
      return resident;
  }
};

// Функция для получения русских названий ролей пользователей
export const getRoleDisplayName = (role: UserRole): string => {
  switch (role) {
    case UserRole.USER:
      return "Пользователь";
    case UserRole.RESIDENT:
      return "Резидент";
    default:
      return role;
  }
};

export interface CreateEventRequest {
  title: string;
  description: string;
  cityId: number;
  address: string;
  broadcastUrl: string;
  participantMax: number;
  registrationStartAt: string;
  registrationEndAt: string;
  format: "online" | "offline";
  startAt: string;
  endAt: string;
  calibers: ResidentCaliber[];
  roles: UserRole[];
  categoryId: number;
  speakers: number[];
  isPublicContent: boolean;
  addressLocationUrl: string;
  clubId?: number | null;
  /** Не передан — бэкенд поставит false */
  participantsFormed?: boolean;
}

export interface UpdateEventRequest {
  title: string;
  description: string;
  cityId: number;
  address: string;
  broadcastUrl: string;
  participantMax: number;
  registrationStartAt: string;
  registrationEndAt: string;
  format: "online" | "offline";
  startAt: string;
  endAt: string;
  calibers: ResidentCaliber[];
  roles: UserRole[];
  categoryId: number;
  speakers: number[];
  isPublicContent: boolean;
  addressLocationUrl: string;
  clubId?: number | null;
  /** Не передан — текущее значение не изменится */
  participantsFormed?: boolean;
}

export interface CategoriesResponse {
  result: boolean;
  categories: EventCategory[];
}

export interface CategoryResponse {
  result: boolean;
  category: EventCategory;
}

export interface CreateCategoryRequest {
  name: string;
}

export interface UpdateCategoryRequest {
  name: string;
}

export interface SpeakersResponse {
  result: boolean;
  speakers: Speaker[];
}

export interface SpeakerResponse {
  result: boolean;
  speaker: Speaker;
}

export interface CreateSpeakerRequest {
  firstName: string;
  lastName: string;
  patronymic: string;
  about: string;
  userId?: string;
  skills: number[];
  /** Не передан — сохранённый контакт сотрётся */
  phoneNumber?: string;
  /** Не передан — сохранённый контакт сотрётся */
  instagramUrl?: string;
}

export interface UpdateSpeakerRequest {
  firstName: string;
  lastName: string;
  patronymic: string;
  about: string;
  userId?: string;
  skills: number[];
  /** Не передан — сохранённый контакт сотрётся */
  phoneNumber?: string;
  /** Не передан — сохранённый контакт сотрётся */
  instagramUrl?: string;
}

export interface SkillsResponse {
  result: boolean;
  skills: Skill[];
}

export interface SkillResponse {
  result: boolean;
  skill: Skill;
}

export interface CreateSkillRequest {
  name: string;
}

export interface UpdateSkillRequest {
  name: string;
}

export interface QRCode {
  createdAt: string;
  expiresAt: string;
  code: string;
  universalLink: string;
}

export interface QRCodesResponse {
  result: boolean;
  codes: QRCode[];
}

export interface QRCodeResponse {
  result: boolean;
  code: QRCode;
}

export interface CreateQRCodeRequest {
  expiresAt: string;
}

export interface UrlResponse {
  result: boolean;
  url: string;
}

// Event Content Types
export enum AccessType {
  CLOSED = 0, // "Доступ закрыт"
  USERS_AND_RESIDENTS = 1, // "Пользователи и резиденты"
  RESIDENTS_ON_EVENT_DATE = 2, // "Резиденты на дату мероприятия"
  RESIDENTS_ON_VIEW = 3, // "Резиденты на момент просмотра"
}

export const getAccessTypeDisplayName = (accessType: AccessType): string => {
  switch (accessType) {
    case AccessType.CLOSED:
      return "Доступ закрыт";
    case AccessType.USERS_AND_RESIDENTS:
      return "Пользователи и резиденты";
    case AccessType.RESIDENTS_ON_EVENT_DATE:
      return "Резиденты на дату мероприятия";
    case AccessType.RESIDENTS_ON_VIEW:
      return "Резиденты на момент просмотра";
    default:
      return "Неизвестно";
  }
};

export enum EventContentSourceType {
  FILE = 1,
  LINK = 2,
}

export const getEventContentSourceTypeDisplayName = (
  sourceTypeId: EventContentSourceType,
): string => {
  switch (sourceTypeId) {
    case EventContentSourceType.FILE:
      return "Файл";
    case EventContentSourceType.LINK:
      return "Внешняя ссылка";
    default:
      return "Неизвестно";
  }
};

export interface EventContentFileInfo {
  name: string;
  size: number;
  uploadAt: string;
}

export interface EventContent {
  id: number;
  createdAt: string;
  updatedAt: string;
  sourceTypeId: EventContentSourceType;
  url: string;
  title: string;
  description: string;
  accessTypeId: AccessType;
  file?: EventContentFileInfo;
}

export interface EventContentResponse {
  result: boolean;
  content: EventContent;
}

export interface EventContentListResponse {
  result: boolean;
  items: EventContent[];
}

export interface UpdateEventContentFileRequest {
  title: string;
  description: string;
  accessTypeId: AccessType;
}

export interface CreateEventContentLinkRequest {
  url: string;
  title: string;
  description: string;
  accessTypeId: AccessType;
}

export interface UpdateEventContentLinkRequest {
  url: string;
  title: string;
  description: string;
  accessTypeId: AccessType;
}

// Event Statistics Types
export interface EventStatUserInfo {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  phoneNumber: string;
  role: "guest" | "user" | "resident";
  caliber?: ResidentCaliber;
}

export interface EventStatItem {
  user: EventStatUserInfo;
  applicationAt?: string;
  participantAt?: string;
  paymentAt?: string;
  amountPaid?: number;
}

export interface EventStatsResponse {
  result: boolean;
  items: EventStatItem[];
}

// Event Applications (состав участников)
export interface EventApplicationUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  phoneNumber?: string | null;
  role: "guest" | "user" | "resident";
  caliber?: ResidentCaliber;
}

export interface EventApplication {
  id: number;
  registeredAt: string;
  user: EventApplicationUser;
  /** Заполнено, если участника записал администратор; у самозаписавшихся отсутствует */
  registeredBy?: EventApplicationUser | null;
}

/** GET /admin/events/{id}/applications */
export interface EventApplicationsResponse {
  result: boolean;
  pageToken?: string | null;
  applications: EventApplication[];
}

/** POST /admin/events/{id}/applications/copy/{sourceEventId} */
export interface EventApplicationsCopyResponse {
  result: boolean;
  /** Уже состоявшие в составе не считаются */
  copiedCount: number;
}

export const getEventUserFullName = (user?: EventApplicationUser | null) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

// Event Prices
export const EVENT_PRICE_TYPE = {
  NON_RESIDENT: 1,
  RESIDENT: 2,
} as const;

export type EventPriceTypeId =
  (typeof EVENT_PRICE_TYPE)[keyof typeof EVENT_PRICE_TYPE];

export const getEventPriceTypeDisplayName = (
  typeId: EventPriceTypeId,
): string => {
  switch (typeId) {
    case EVENT_PRICE_TYPE.NON_RESIDENT:
      return "Для не резидентов";
    case EVENT_PRICE_TYPE.RESIDENT:
      return "Для резидентов";
    default:
      return "Неизвестно";
  }
};

export interface Currency {
  id: number;
  code: string;
  symbol: string;
  name: string;
}

export const CURRENCIES: Currency[] = [
  { id: 398, code: "KZT", symbol: "₸", name: "Тенге" },
  { id: 840, code: "USD", symbol: "$", name: "Доллар США" },
  { id: 978, code: "EUR", symbol: "€", name: "Евро" },
];

export interface EventPrice {
  id: number;
  typeId: EventPriceTypeId;
  price: {
    unicode: string;
    value: number;
  };
  createdAt: string;
  startAt: string;
}

export interface EventPricesResponse {
  result: boolean;
  prices: EventPrice[];
}

export interface EventPriceResponse {
  result: boolean;
  price: EventPrice;
}

export interface CreateEventPriceRequest {
  typeId: EventPriceTypeId;
  currencyId: number;
  value: number;
  startAt: string;
}

export interface UpdateEventPriceRequest {
  typeId?: EventPriceTypeId;
  currencyId?: number;
  value?: number;
  startAt?: string;
}
